import React, { useState, useEffect, useContext } from 'react'
import { Layout, Card, Table, Badge, Input, Typography, Button, Tag, Tooltip } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../auth/AuthContext'
import { axiosForm } from '../../axiosInstances'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import DashboardLayout from './DashboardLayout'

dayjs.extend(utc)
dayjs.extend(timezone)

const { Content } = Layout
const { Title } = Typography

// Format date as "DD/MM/YYYY, HH:mm"
const formatUserFriendlyDate = (isoDate) =>
  isoDate ? dayjs(isoDate).tz(dayjs.tz.guess()).format('DD/MM/YYYY, HH:mm') : ''

// Show only days remaining until due date (or Expiré !)
const getDaysRemaining = (dueDateString) => {
  if (!dueDateString) return null
  const now = dayjs().tz(dayjs.tz.guess())
  const due = dayjs.utc(dueDateString).tz(dayjs.tz.guess())
  if (due.isBefore(now, 'minute')) {
    return <span style={{ color: '#d4380d', fontWeight: 500 }}>Expiré !</span>
  }
  const diffDays = Math.ceil(due.diff(now, 'minute') / (60 * 24))
  return (
    <span style={{ color: diffDays <= 2 ? '#faad14' : '#389e0d', fontWeight: 500 }}>
      {diffDays} jours restants
    </span>
  )
}

const Dashboard = () => {
  const [rfqs, setRfqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchId, setSearchId] = useState("")
  const [filteredRfqs, setFilteredRfqs] = useState([])
  const navigate = useNavigate()
  const { user, token } = useContext(AuthContext)

  useEffect(() => {
    if (!token || !user) return
    setLoading(true)
    
    axiosForm.get(`/authentication/api/vendor/rfqs/?status=sent,resubmission_requested&vendor_name=${encodeURIComponent(user.vendor_name)}`)
      .then(response => {
        setRfqs(response.data.rfqs || [])
        setFilteredRfqs(response.data.rfqs || [])
        setLoading(false)
      })
      .catch(error => {
        console.error('Error fetching RFQs:', error)
        setLoading(false)
      })
  }, [token, user])

  useEffect(() => {
    if (!searchId) {
      setFilteredRfqs(rfqs)
    } else {
      setFilteredRfqs(
        rfqs.filter(rfq =>
          rfq.rfq_number.toLowerCase().includes(searchId.toLowerCase())
        )
      )
    }
  }, [searchId, rfqs])

  const columns = [
    {
      title: 'Numéro RFQ',
      dataIndex: 'rfq_number',
      key: 'rfq_number',
      render: (rfq_number, rfq) => (
        <span style={{ fontSize: 18, fontWeight: 500 }}>
          {rfq_number}
          {rfq.resubmission_requested && (
            <Tooltip title="Le client demande une correction de votre offre">
              <Tag color="orange" style={{ marginLeft: 8, fontWeight: 500 }}>
                Correction demandée
              </Tag>
            </Tooltip>
          )}
        </span>
      )
    },
    {
      title: 'Articles',
      dataIndex: 'rfq_items_count',
      key: 'rfq_items_count',
      align: 'center',
      render: count => (
        <Badge count={count} style={{ backgroundColor: '#1890ff' }} showZero />
      ),
    },
    {
      title: 'Envoyé le',
      dataIndex: 'sent_date',
      key: 'sent_date',
      align: 'center',
      render: dateString => formatUserFriendlyDate(dateString),
    },
    {
      title: 'Échéance',
      dataIndex: 'due_date',
      key: 'due_date',
      align: 'center',
      render: dateString => (
        <span>
          {formatUserFriendlyDate(dateString)} <br />
          {getDaysRemaining(dateString)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, rfq) => (
        <Button
          type="primary"
          size="small"
          danger={rfq.resubmission_requested}
          onClick={() => navigate(`/rfq/${rfq.id}/offer`)}
        >
          {rfq.resubmission_requested ? "Corriger l'offre" : 'Répondre'}
        </Button>
      ),
    },
  ]

  return (
    <DashboardLayout selectedMenuKey="ongoing">
      <Content style={{ margin: '40px 24px', overflow: 'auto' }}>
        <Card
          style={{ boxShadow: '0 2px 8px #f0f1f2', position: 'relative', maxWidth: 1100, margin: '0 auto' }}
          bodyStyle={{ paddingTop: 40, paddingBottom: 24, paddingLeft: 24, paddingRight: 24 }}
        >
          <Title level={4} style={{ marginBottom: 16, fontWeight: 700 }}>
            Mes demandes de devis <Badge count={filteredRfqs.length} style={{ background: '#1890ff' }} />
          </Title>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 12 }}>
            <Input
              placeholder="Numéro RFQ"
              prefix={<SearchOutlined />}
              value={searchId}
              allowClear
              onChange={e => setSearchId(e.target.value)}
              style={{ width: 300 }}
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredRfqs}
            rowKey="id"
            loading={loading}
            pagination={false}
            locale={{
              emptyText: <span className="text-muted">Aucune demande de devis reçue</span>
            }}
          />
        </Card>
      </Content>
    </DashboardLayout>
  )
}

export default Dashboard
